<template>
    <div class="segment-classifications-manager">
        <!-- Header -->
        <div class="mb-3">
            <h6 class="mb-3">
                <i class="fas fa-sitemap me-2"></i>
                Classificações Hierárquicas
            </h6>
            <button type="button" class="btn btn-sm btn-primary" @click="openAddClassificationModal(null)">
                <i class="fas fa-plus me-2"></i>
                Adicionar Classificação
            </button>
        </div>

        <!-- Classifications Tree -->
        <div v-if="classifications.length > 0" class="classifications-tree">
            <classification-tree-item 
                v-for="classification in classifications" 
                :key="classification.id"
                :classification="classification"
                :segment-id="segmentId"
                @add-child="openAddClassificationModal"
                @edit="openEditClassificationModal"
                @delete="deleteClassification"
                @refresh="loadClassifications"
            />
        </div>
        <div v-else class="alert alert-info">
            <i class="fas fa-info-circle me-2"></i>
            Nenhuma classificação adicionada ainda. Clique no botão acima para começar.
        </div>

        <!-- Modal para Adicionar/Editar Classificação -->
        <div class="modal fade" id="classificationModal" tabindex="-1">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">
                            {{ editingClassification ? 'Editar Classificação' : 'Adicionar Classificação' }}
                        </h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                        <form @submit.prevent="saveClassification">
                            <!-- Name -->
                            <div class="mb-3">
                                <label class="form-label">Nome *</label>
                                <input 
                                    v-model="formData.name" 
                                    type="text" 
                                    class="form-control"
                                    :class="{ 'is-invalid': errors.name }"
                                    required
                                />
                                <small v-if="errors.name" class="text-danger">{{ errors.name }}</small>
                            </div>

                            <!-- Type -->
                            <div class="mb-3">
                                <label class="form-label">Tipo de Campo *</label>
                                <select v-model="formData.type" class="form-select" required>
                                    <option value="select">Seleção</option>
                                    <option value="text">Texto</option>
                                    <option value="number">Número</option>
                                    <option value="date">Data</option>
                                </select>
                            </div>

                            <!-- Is Required -->
                            <div class="mb-3">
                                <div class="form-check">
                                    <input 
                                        v-model="formData.is_required" 
                                        type="checkbox" 
                                        class="form-check-input"
                                        id="isRequired"
                                    />
                                    <label class="form-check-label" for="isRequired">
                                        Obrigatória
                                    </label>
                                </div>
                            </div>

                            <!-- Is Price Modifier -->
                            <div class="mb-3">
                                <div class="form-check">
                                    <input 
                                        v-model="formData.is_price_modifier" 
                                        type="checkbox" 
                                        class="form-check-input"
                                        id="isPriceModifier"
                                    />
                                    <label class="form-check-label" for="isPriceModifier">
                                        Modifica Preço
                                    </label>
                                </div>
                            </div>

                            <!-- Parent Classification -->
                            <div class="mb-3">
                                <label class="form-label">Classificação Pai (opcional)</label>
                                <select v-model="formData.parent_id" class="form-select">
                                    <option :value="null">-- Nenhuma (Raiz) --</option>
                                    <option 
                                        v-for="classification in availableParents" 
                                        :key="classification.id"
                                        :value="classification.id"
                                    >
                                        {{ classification.name }}
                                    </option>
                                </select>
                            </div>
                        </form>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
                        <button type="button" class="btn btn-primary" @click="saveClassification" :disabled="isSaving">
                            <span v-if="isSaving" class="spinner-border spinner-border-sm me-2"></span>
                            {{ editingClassification ? 'Atualizar' : 'Adicionar' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ClassificationTreeItem from './ClassificationTreeItem.vue';

export default {
    name: 'SegmentClassificationsManager',
    components: {
        ClassificationTreeItem
    },
    props: {
        segmentId: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            classifications: [],
            formData: {
                name: '',
                type: 'select',
                is_required: false,
                is_price_modifier: false,
                parent_id: null
            },
            editingClassification: null,
            errors: {},
            isSaving: false,
            modal: null
        };
    },
    computed: {
        availableParents() {
            return this.flattenClassifications(this.classifications);
        }
    },
    methods: {
        loadClassifications() {
            axios.get(`/admin/segments/${this.segmentId}/classifications`)
                .then(response => {
                    if (response.data.success) {
                        this.classifications = response.data.data;
                    }
                })
                .catch(error => {
                    console.error('Erro ao carregar classificações:', error);
                });
        },

        openAddClassificationModal(parentId = null) {
            this.editingClassification = null;
            this.formData = {
                name: '',
                type: 'select',
                is_required: false,
                is_price_modifier: false,
                parent_id: parentId
            };
            this.errors = {};
            this.modal.show();
        },

        openEditClassificationModal(classification) {
            this.editingClassification = classification;
            this.formData = {
                name: classification.name,
                type: classification.type,
                is_required: classification.is_required,
                is_price_modifier: classification.is_price_modifier,
                parent_id: classification.parent_id
            };
            this.errors = {};
            this.modal.show();
        },

        saveClassification() {
            this.isSaving = true;
            this.errors = {};

            const url = this.editingClassification 
                ? `/admin/classifications/${this.editingClassification.id}`
                : `/admin/segments/${this.segmentId}/classifications`;

            const method = this.editingClassification ? 'put' : 'post';

            axios[method](url, this.formData)
                .then(response => {
                    if (response.data.success) {
                        this.modal.hide();
                        this.loadClassifications();
                        this.$emit('saved');
                    }
                })
                .catch(error => {
                    if (error.response?.data?.errors) {
                        this.errors = error.response.data.errors;
                    }
                    console.error('Erro ao salvar classificação:', error);
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },

        deleteClassification(classificationId) {
            if (confirm('Tem certeza que deseja deletar esta classificação? Todas as sub-classificações também serão deletadas.')) {
                axios.delete(`/admin/classifications/${classificationId}`)
                    .then(response => {
                        if (response.data.success) {
                            this.loadClassifications();
                        }
                    })
                    .catch(error => {
                        console.error('Erro ao deletar classificação:', error);
                        alert('Erro ao deletar classificação');
                    });
            }
        },

        flattenClassifications(classifications, prefix = '') {
            let result = [];
            classifications.forEach(classification => {
                result.push({
                    ...classification,
                    name: prefix + classification.name
                });
                if (classification.children && classification.children.length > 0) {
                    result = result.concat(this.flattenClassifications(classification.children, prefix + '→ '));
                }
            });
            return result;
        }
    },
    mounted() {
        this.modal = new bootstrap.Modal(document.getElementById('classificationModal'));
        this.loadClassifications();
    }
};
</script>

<style scoped>
.segment-classifications-manager {
    padding: 15px 0;
    border-top: 1px solid #dee2e6;
    margin-top: 20px;
}

.classifications-tree {
    margin-top: 15px;
}

.alert {
    margin-top: 15px;
}

.modal-body {
    max-height: 500px;
    overflow-y: auto;
}

.form-check {
    padding-left: 0;
}

.form-check-input {
    margin-left: 0;
    margin-right: 8px;
}
</style>
